import type { AnyTrainer, SimParams } from './types';

export type ControlCommand =
  { type: 'erg'; watts: number } | { type: 'sim'; params: SimParams } | { type: 'none' };

/**
 * Keeps the desired trainer command and applies it with change detection.
 * Rate limiting and coalescing happen in the trainer's write queue (SPEC §4.1).
 * After a reconnect the last command is re-applied (the trainer re-requests control in its setup).
 */
export class TrainerController {
  private trainer: AnyTrainer | null = null;
  private desired: ControlCommand = { type: 'none' };
  private lastSentErg: number | null = null;
  private lastSentSim: SimParams | null = null;
  private unsubConn: (() => void) | null = null;
  /** Commands actually handed to the trainer (debug overlay / tests). */
  sent = 0;

  attach(trainer: AnyTrainer | null): void {
    this.unsubConn?.();
    this.unsubConn = null;
    this.trainer = trainer;
    this.lastSentErg = null;
    this.lastSentSim = null;
    if (trainer) {
      this.unsubConn = trainer.onConnectionChange((connected) => {
        if (connected) this.reapply();
        else {
          this.lastSentErg = null;
          this.lastSentSim = null;
        }
      });
      if (trainer.connected) this.reapply();
    }
  }

  get current(): ControlCommand {
    return this.desired;
  }

  get attached(): AnyTrainer | null {
    return this.trainer;
  }

  setErg(watts: number): void {
    const w = Math.max(0, Math.round(watts));
    this.desired = { type: 'erg', watts: w };
    this.lastSentSim = null;
    if (this.lastSentErg === w) return;
    this.push();
  }

  setSim(params: SimParams): void {
    this.desired = { type: 'sim', params };
    this.lastSentErg = null;
    const l = this.lastSentSim;
    if (
      l &&
      Math.abs(l.gradePct - params.gradePct) < 0.01 &&
      Math.abs(l.crr - params.crr) < 0.00005 &&
      Math.abs(l.cwKgPerM - params.cwKgPerM) < 0.005 &&
      Math.abs(l.windMps - params.windMps) < 0.001
    ) {
      return;
    }
    this.push();
  }

  clear(): void {
    this.desired = { type: 'none' };
  }

  reapply(): void {
    this.lastSentErg = null;
    this.lastSentSim = null;
    this.push();
  }

  private push(): void {
    const t = this.trainer;
    if (!t || !t.connected) return;
    const d = this.desired;
    if (d.type === 'erg' && t.caps.erg) {
      this.lastSentErg = d.watts;
      this.sent++;
      void t.setErg(d.watts);
    } else if (d.type === 'sim' && t.caps.sim) {
      this.lastSentSim = d.params;
      this.sent++;
      void t.setSim(d.params);
    }
  }
}
